<template>
  <SmDialog
    v-model:visibility="visible"
    :title="i18n.t('editCompany')"
    size="small">
    <div class="flex flex-col items-start gap-4">
      <SmDialogMessage
        :message="error.message"
        :visible="error.visible"
        type="error" />

      <div style="width: 99%">
        <span class="font-medium text-gray-500">{{ i18n.t('name') }}</span>
        <SmInput
          class="pt-2"
          v-model="companyName"
          size="medium"
          outline
          design="form" />
      </div>
    </div>

    <template #footer>
      <div style="margin-top: 1rem; display: flex; gap: 1rem">
        <SmButton outline @click="visible = false">
          {{ i18n.t('cancel') }}
        </SmButton>
        <SmButton
          type="primary"
          :loading="loading"
          :disabled="
            !companyName ||
            companyName === props.company.name ||
            companyName.trim().length === 0
          "
          @click="updateCompanyName">
          {{ i18n.t('save') }}
        </SmButton>
      </div>
    </template>
  </SmDialog>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { AvailableCompany, CompaniesService } from '@/client'
  import { sendToast } from '../sm/SmNotification'
  import { useSessionStore } from '@/stores/sessionStore'

  const i18n = useI18n()

  const visible = defineModel('visible', {
    default: false,
    type: Boolean,
  })
  const companyName = defineModel('companyName', {
    default: '',
    type: String,
  })

  interface Props {
    company: AvailableCompany
  }
  const props = defineProps<Props>()

  const loading = ref(false)

  const error = ref({
    message: '',
    visible: false,
  })

  const sessionStore = useSessionStore()

  const emit = defineEmits(['companyUpdated'])

  async function updateCompanyName() {
    if (loading.value) return
    loading.value = true

    try {
      await CompaniesService.updateCompanyNameCompaniesCompanyIdNamePatch({
        companyId: props.company.company_id,
        requestBody: {
          name: companyName.value,
        },
      })

      const updatedCompany: AvailableCompany = {
        ...props.company,
        name: companyName.value,
      }

      if (
        sessionStore.currentCompany?.company_id === props.company.company_id
      ) {
        sessionStore.currentCompany = {
          ...sessionStore.currentCompany,
          name: companyName.value,
        }
      }

      sendToast(i18n.t('companyUpdated'), undefined, 'success')
      emit('companyUpdated', updatedCompany)
      visible.value = false
    } catch (e) {
      error.value = {
        message: i18n.t('errorEditingCompany'),
        visible: true,
      }
    } finally {
      loading.value = false
    }
  }

  watch(
    () => props.company,
    (newCompany) => {
      if (newCompany) {
        companyName.value = newCompany.name
      }
    },
    { immediate: true }
  )

  watch(
    () => visible.value,
    (newValue) => {
      if (!newValue) {
        error.value = {
          message: '',
          visible: false,
        }
      }
    }
  )
</script>
